import { useMemo } from "react";
import { ACCRUED_BILLING_NOUN_BY_SOURCE } from "@common/helpers/domains/accounting/accruedBillingRule";
import {
    useScheduleTemplate,
    useWorkspaceScheduleApplications,
} from "@api/scheduleTemplate.api";
import { ScheduleLifecycleChange } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleApplicationListItem } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import {
    ScheduleLifecycleVerb,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { Loader } from "@design-system/Loader/Loader";
import { getAvailableScheduleLifecycleVerbs } from "../applications/scheduleApplicationStatusDisplay";
import { toSpecificDateMilestoneBody } from "../scheduleMilestoneFormHelpers";
import { buildAccruedBilling } from "../scheduleApplyAccruedHelpers";
import { useScheduleApplyPreview } from "../useScheduleApplyPreview";
import {
    ScheduleExpandableModal,
    ScheduleModalLoadError,
} from "./ScheduleExpandableModal";
import { ScheduleManualBody } from "./ScheduleManualBody";
import { ScheduleManualModalFrame } from "./ScheduleManualModalFrame";
import { ScheduleManualPreviewPane } from "./ScheduleManualPreviewPane";
import { followAccruedDraftTermStart } from "./scheduleAccruedDraftStart";
import { ScheduleDraft } from "./scheduleDraft";
import { buildScheduleEditChange } from "./scheduleEditChange";
import { useScheduleDraftState } from "./useScheduleDraftState";

const TITLE = "View schedule";
const EDIT_SUBTITLE =
    "Review the recognition schedule; expand to full screen to preview it";
const READ_ONLY_SUBTITLE = "This schedule can no longer be changed";

export interface ScheduleEditTarget {
    applicationId: string;
    entityId: number;
}

interface ScheduleEditModalProps {
    target: ScheduleEditTarget | null;
    onHide: () => void;
    /** Hands the edit to the Change flow, which asks how to treat posted entries. */
    onRequestChange: (change: ScheduleLifecycleChange) => void;
}

export const ScheduleEditModal = ({
    target,
    onHide,
    onRequestChange,
}: ScheduleEditModalProps) => (
    <ScheduleExpandableModal item={target} onHide={onHide}>
        {({ item, isExpanded, onExpandedChange }) => (
            <ScheduleEditModalBody
                target={item}
                isExpanded={isExpanded}
                onExpandedChange={onExpandedChange}
                onHide={onHide}
                onRequestChange={onRequestChange}
            />
        )}
    </ScheduleExpandableModal>
);

interface ScheduleEditBodyProps {
    isExpanded: boolean;
    onExpandedChange: (isExpanded: boolean) => void;
    onHide: () => void;
    onRequestChange: (change: ScheduleLifecycleChange) => void;
}

const ScheduleEditModalBody = ({
    target,
    ...bodyProps
}: ScheduleEditBodyProps & { target: ScheduleEditTarget }) => {
    const { applications, isLoading, isError } =
        useWorkspaceScheduleApplications({
            applicationId: target.applicationId,
        });
    const application = applications?.find(
        (item) => item.id === target.applicationId,
    );
    if (application) {
        return <ScheduleEditContent application={application} {...bodyProps} />;
    }
    if (isLoading && !isError) {
        return <Loader />;
    }
    return <ScheduleModalLoadError title={TITLE} onHide={bodyProps.onHide} />;
};

const ScheduleEditContent = ({
    application,
    isExpanded,
    onExpandedChange,
    onHide,
    onRequestChange,
}: ScheduleEditBodyProps & { application: ScheduleApplicationListItem }) => {
    const templateQuery = useScheduleTemplate(application.scheduleTemplateId);
    const canChange = getAvailableScheduleLifecycleVerbs({
        status: application.status,
        isMilestone: application.isMilestone ?? false,
    }).includes(ScheduleLifecycleVerb.change);
    const appliedMilestoneRows = useMemo(
        () =>
            application.isMilestone
                ? application.periods
                      .flatMap((period) =>
                          period.milestone ? [period.milestone] : [],
                      )
                      .sort((left, right) => left.sortOrder - right.sortOrder)
                      .map(toSpecificDateMilestoneBody)
                : undefined,
        [application],
    );
    const isManual = application.sourceType === ScheduleSourceType.manual;
    const sourceDate = isManual ? null : application.source.date;
    const term = {
        length: application.termLength,
        unit: application.termUnit,
    };
    const draftState = useScheduleDraftState({
        template: templateQuery.data,
        entityId: application.entityId,
        start: {
            amount: application.amount,
            termStart: application.termStart,
            specificDate: application.specificDate ?? "",
            sourceDate,
            term,
        },
        fixedAmount: isManual ? undefined : application.amount,
    });
    const { resolved, overrides } = draftState;
    const preview = useScheduleApplyPreview({
        template: templateQuery.data,
        amount: resolved?.amount ?? 0,
        termStart: resolved?.termStart ?? application.termStart,
        sourceDate,
        specificDate: resolved?.specificDate ?? "",
        term: draftState.draft
            ? {
                  length: draftState.draft.termLength,
                  unit: draftState.draft.termUnit,
              }
            : term,
        components: resolved?.components,
        milestones: appliedMilestoneRows,
        billing: buildAccruedBilling({
            sourceDate,
            amount: application.amount,
            noun: ACCRUED_BILLING_NOUN_BY_SOURCE[application.sourceType],
        }),
    });
    const onDraftChange = (next: ScheduleDraft) =>
        draftState.setDraft(
            draftState.draft
                ? followAccruedDraftTermStart({
                      subModes: application,
                      sourceType: application.sourceType,
                      sourceDate,
                      previous: draftState.draft,
                      next,
                  })
                : next,
        );
    if (templateQuery.isError) {
        return <ScheduleModalLoadError title={TITLE} onHide={onHide} />;
    }
    const change = resolved
        ? buildScheduleEditChange({
              current: application,
              resolved,
              overrides,
          })
        : {};
    const canSubmit =
        canChange &&
        Object.keys(change).length > 0 &&
        draftState.error === null &&
        preview.errorMessage === null &&
        preview.isSettled;

    return (
        <ScheduleManualModalFrame
            title={TITLE}
            subtitle={`${application.source.label} · ${canChange ? EDIT_SUBTITLE : READ_ONLY_SUBTITLE}`}
            isExpanded={isExpanded}
            onExpandedChange={onExpandedChange}
            preview={<ScheduleManualPreviewPane preview={preview} />}
            secondaryLabel="Cancel"
            onSecondary={onHide}
            expandedSecondaryLabel="Back"
            submitLabel="Apply edit"
            isSubmitDisabled={!canSubmit}
            isSubmitting={false}
            onSubmit={() => {
                if (canSubmit) {
                    onRequestChange(change);
                }
            }}
        >
            <ScheduleManualBody
                template={templateQuery.data}
                entityId={application.entityId}
                draft={draftState.draft}
                onDraftChange={onDraftChange}
                draftError={
                    draftState.error ??
                    (isExpanded ? null : preview.errorMessage)
                }
                isEditable={canChange}
                isSpecificDateEditable={false}
                isHoldingAccountEditable={false}
                layout={isExpanded ? "drawer" : "modal"}
            />
        </ScheduleManualModalFrame>
    );
};
